import { IS_WEB } from "./config";
import {
  profileEditorUnameEl,
  profileEditorNameEl,
  profileEditorPhotoEl,
  profileEditorScrollContainerEl,
  profileEditorAboutMeTextAreaEl,
  profileEditorDatePickerBtnEl,
  menuProfilePhotoImgEl,
  profileEditorOkEl,
  profileEditorDialogEl,
  profileEditorCancelEl,
  menuProfilePhotoUnameEl,
  profileEditorTiktokEl,
  profileEditorRedditEl,
  profileEditorDiscordEl,
  profileEditorIgEl,
  communityProfileFeedEl,
} from "./elements";
import { addTouchEvent } from "./events";
import { Camera, FilePicker, FirebaseStorage } from "./plugins";
import store from "./store";
import { addScrollContainerFocusEventCallback } from "./utils";
import datepicker from "js-datepicker";
import { CameraResultType } from "@capacitor/camera";
import { withSpinner } from "./spinner";
import { getEventParams } from "./analytics";
import { catchErrorWithStack, throwErrorWithStack } from "./crashlytics";
import { showInfoSnackbar } from "./snackbar";
import { updateProfile } from "./profile";
import cachedFirestore from "./cachedFirestore";
import { validateInput } from "./validate";
import { Capacitor } from "@capacitor/core";
import { autoTranslateElements } from "./translate";
import { getLogger } from "./logging";

const log = getLogger("ProfileEditor");

function validateInstagramUsername(username) {
  return /^[A-Za-z0-9._]{1,30}$/.test(username);
}

function validateTikTokUsername(username) {
  return (
    /^[A-Za-z0-9._]{2,24}$/.test(username) &&
    !username.startsWith(".") &&
    !username.endsWith(".") &&
    !username.includes("..")
  );
}

function validateDiscordUsername(username) {
  return /^[A-Za-z0-9_.]{2,32}$/.test(username) && !username.includes("..");
}

function validateRedditUsername(username) {
  return /^[A-Za-z0-9_]{3,20}$/.test(username);
}

export function emailToUsername(email) {
  // Extract the part before the '@' symbol
  let username = email.split("@")[0];

  // Replace invalid characters with '_'
  username = username.replace(/[^a-zA-Z0-9._]/g, "_");

  // Limit the username to 15 characters
  if (username.length > 15) {
    username = username.substring(0, 15);
  }

  // Ensure the username is at least 3 characters long
  // If it's shorter, append numbers starting from 1
  let number = 1;
  while (username.length < 3) {
    username += number.toString();
    number++;
    // Ensure total length does not exceed 20 characters
    if (username.length > 20) {
      username = username.substring(0, 20);
      break;
    }
  }

  return username;
}

export function validateUsername(username) {
  // Check if username is 1-15 characters long and contains only allowed characters
  return /^[a-zA-Z0-9._]{3,15}$/.test(username);
}

async function validateUsernameExists(uname) {
  if (await unameExists(uname)) return false;
  return validateUsername(uname);
}

export function validateName(name) {
  // Regular expression to test if name is 2-30 characters long,
  // contains only letters, periods, and whitespace
  // (no whitespace at the beginning or end)
  return (
    /^[A-Za-z.][A-Za-z .]*[A-Za-z.]$/.test(name) &&
    name.length >= 2 &&
    name.length <= 30
  );
}

export function validateAboutMe(aboutMeText) {
  return aboutMeText.length >= 5 && aboutMeText.length <= 250;
}

let imgUriGlob;
let imgPathGlob;
let unameGlob;
let nameGlob;
let bdateGlob;
let aboutmeGlob;
let tiktokGlob;
let redditGlob;
let discordGlob;
let igGlob;
let editUnameGlob;

async function checkInput(unameCheck = false) {
  const fields = [
    {
      element: profileEditorUnameEl,
      value: unameGlob,
      validate: validateUsername,
      characterLimit: 15,
      disallowEmpty: true,
    },
    {
      element: profileEditorNameEl,
      value: nameGlob,
      validate: validateName,
      characterLimit: 30,
    },
    {
      element: profileEditorAboutMeTextAreaEl,
      value: aboutmeGlob,
      validate: validateAboutMe,
      characterLimit: 250,
    },
    {
      element: profileEditorTiktokEl,
      value: tiktokGlob,
      validate: validateTikTokUsername,
      characterLimit: 24,
    },
    {
      element: profileEditorRedditEl,
      value: redditGlob,
      validate: validateRedditUsername,
      characterLimit: 20,
    },
    {
      element: profileEditorDiscordEl,
      value: discordGlob,
      validate: validateDiscordUsername,
      characterLimit: 32,
    },
    {
      element: profileEditorIgEl,
      value: igGlob,
      validate: validateInstagramUsername,
      characterLimit: 30,
    },
  ];
  if (unameCheck)
    fields.push({
      element: profileEditorUnameEl,
      value: unameGlob,
      validate: validateUsernameExists,
      characterLimit: 15,
      disallowEmpty: true,
      secondErrorMsg: true,
    });
  const disable = (await validateInput(fields)) || !unameGlob;
  disableSave(disable);
  return disable;
}

const disableSave = (disable) => {
  if (disable) {
    profileEditorOkEl.setAttribute("disabled", "disabled");
    profileEditorOkEl.style.backgroundColor = "#E0E0E0";
    profileEditorOkEl.style.color = "#9E9E9E";
    profileEditorOkEl.style.borderColor = "#9E9E9E";
    profileEditorOkEl.classList.remove("button");
  } else {
    profileEditorOkEl.removeAttribute("disabled");
    profileEditorOkEl.style.backgroundColor = "#D3FF02";
    profileEditorOkEl.style.color = "black";
    profileEditorOkEl.style.borderColor = "black";
    profileEditorOkEl.classList.add("button");
  }
};

// profileTextAreaEl.addEventListener("input", (event) => {
//     _review = event.target.value;
//     checkInput();
//   });

export function showProfileEditor(editUname = false) {
  editUname = true;
  editUnameGlob = editUname;
  unameGlob = store.profile.uname || emailToUsername(store.user.email);
  nameGlob = store.profile.name || store.user.displayName || "";
  bdateGlob = store.profile.bdate || null;
  aboutmeGlob = store.profile.aboutme || "";
  tiktokGlob = store.profile.tiktok || "";
  redditGlob = store.profile.reddit || "";
  discordGlob = store.profile.discord || "";
  igGlob = store.profile.ig || "";

  if (bdateGlob)
    profileEditorDatePickerBtnEl.textContent =
      "🗓️ " +
      new Date(bdateGlob).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });

  autoTranslateElements(profileEditorDatePickerBtnEl);

  const dateSelected = bdateGlob ? new Date(bdateGlob) : new Date(2000, 0, 1);

  if (pickerGlob) pickerGlob.remove();
  pickerGlob = datepicker(profileEditorDatePickerBtnEl, {
    onSelect: (instance, date) => {
      profileEditorDatePickerBtnEl.textContent =
        "🗓️ " +
        date.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        });
      bdateGlob = date;
      autoTranslateElements(profileEditorDatePickerBtnEl);
    },
    startDate: dateSelected,
    dateSelected,
    minDate,
    maxDate,
    position: "c",
    showAllDates: true,
  });

  if (!editUname && store.profile.uname) {
    // profileEditorUnameEl.style.display = "none";
    profileEditorUnameEl.parentElement.style.visibility = "hidden";
    profileEditorCancelEl.style.backgroundColor = null;
  } else {
    // profileEditorUnameEl.style.display = "initial";
    profileEditorUnameEl.parentElement.style.visibility = "visible";
    profileEditorCancelEl.style.backgroundColor = "#F8616A";
  }
  profileEditorDialogEl.style.display = "initial";

  profileEditorUnameEl.setAttribute("value", unameGlob);
  unameGlob = profileEditorUnameEl.getAttribute("value");

  profileEditorNameEl.setAttribute("value", nameGlob);
  nameGlob = profileEditorNameEl.getAttribute("value");

  if (aboutmeGlob) profileEditorAboutMeTextAreaEl.innerHTML = aboutmeGlob;

  profileEditorTiktokEl.setAttribute("value", tiktokGlob);
  tiktokGlob = profileEditorTiktokEl.getAttribute("value");

  profileEditorRedditEl.setAttribute("value", redditGlob);
  redditGlob = profileEditorRedditEl.getAttribute("value");

  profileEditorDiscordEl.setAttribute("value", discordGlob);
  discordGlob = profileEditorDiscordEl.getAttribute("value");

  profileEditorIgEl.setAttribute("value", igGlob);
  igGlob = profileEditorIgEl.getAttribute("value");

  profileEditorPhotoEl.src =
    store.profile.thumbnailUrl || store.user.photoUrl || "";
  menuProfilePhotoImgEl.src =
    store.profile.thumbnailUrl || store.user.photoUrl || "";
  checkInput();
}
// showProfileEditor(true);

profileEditorUnameEl.addEventListener("input", (event) => {
  unameGlob = event.target.value;
  checkInput();
});
profileEditorNameEl.addEventListener("input", (event) => {
  nameGlob = event.target.value;
  checkInput();
});
profileEditorAboutMeTextAreaEl.addEventListener("input", (event) => {
  aboutmeGlob = event.target.value;
  checkInput();
});
profileEditorTiktokEl.addEventListener("input", (event) => {
  tiktokGlob = event.target.value;
  checkInput();
});
profileEditorRedditEl.addEventListener("input", (event) => {
  redditGlob = event.target.value;
  checkInput();
});
profileEditorDiscordEl.addEventListener("input", (event) => {
  discordGlob = event.target.value;
  checkInput();
});
profileEditorIgEl.addEventListener("input", (event) => {
  igGlob = event.target.value;
  checkInput();
});

const uploadFile = async (options, cb) => {
  return new Promise(async (resolve, reject) => {
    await FirebaseStorage.uploadFile(options, (event, error) => {
      cb && cb(event);
      if (error) {
        reject(error);
      } else if (event?.completed) {
        resolve();
      }
    });
  });
};

addTouchEvent(profileEditorPhotoEl, async () => {
  try {
    if (IS_WEB) {
      let { webPath } = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        width: 512,
        height: 512,
      });
      imgUriGlob = webPath;
    } else {
      const { files } = await FilePicker.pickImages({ multiple: false });
      const file = files[0];
      imgPathGlob = file.path;
      imgUriGlob = Capacitor.convertFileSrc(file.path);
    }
    profileEditorPhotoEl.src = imgUriGlob;
  } catch (error) {
    throwErrorWithStack(error, true);
  }
});

async function saveProfilePhoto() {
  if (!imgUriGlob) imgUriGlob = profileEditorPhotoEl.getAttribute("src");
  if (!imgUriGlob) return;
  const path = `profile-photo/${store.user.uid}.jpeg`;
  let downloadUrl;
  if (imgUriGlob.startsWith("https://firebasestorage.googleapis.com")) {
    downloadUrl = imgUriGlob;
  } else {
    if (IS_WEB) {
      const blob = await fetch(imgUriGlob).then((r) => r.blob());
      await withSpinner(
        uploadFile({
          blob,
          path,
        }),
        "Uploading..."
      );
    } else {
      if (!imgPathGlob) return;
      await withSpinner(
        uploadFile({
          uri: imgPathGlob,
          path,
        }),
        "Uploading..."
      );
    }
    await withSpinner(
      FirebaseStorage.updateMetadata({
        metadata: {
          cacheControl: "public, max-age=604800", // 7 days
          customMetadata: {},
        },
        path,
      })
    );
    const resp = await FirebaseStorage.getDownloadUrl({
      path,
    });
    downloadUrl = resp.downloadUrl;
  }
  if (downloadUrl) {
    profileEditorPhotoEl.src = downloadUrl;
    menuProfilePhotoImgEl.src = downloadUrl;
  }
  return downloadUrl;
}

[
  profileEditorUnameEl,
  profileEditorNameEl,
  profileEditorAboutMeTextAreaEl,
  profileEditorTiktokEl,
  profileEditorRedditEl,
  profileEditorDiscordEl,
  profileEditorIgEl,
].forEach((el) =>
  addScrollContainerFocusEventCallback(
    el,
    profileEditorScrollContainerEl,
    false
  )
);
addScrollContainerFocusEventCallback(
  profileEditorDatePickerBtnEl,
  profileEditorScrollContainerEl,
  false,
  "click"
);

const minDate = new Date();
minDate.setFullYear(minDate.getFullYear() - 100);
const maxDate = new Date();
maxDate.setFullYear(maxDate.getFullYear() - 13);
let pickerGlob;

addTouchEvent(profileEditorDatePickerBtnEl, (e) => {
  e.stopPropagation();
  const isHidden = pickerGlob.calendarContainer.classList.contains("qs-hidden");
  pickerGlob[isHidden ? "show" : "hide"]();
});

const saveProfile = async () => {
  await checkInput(editUnameGlob);
  if (profileEditorOkEl.hasAttribute("disabled")) return;
  let downloadUrl;
  try {
    downloadUrl = await saveProfilePhoto();
  } catch (error) {
    const newError = new Error("Error saving profile photo", { cause: error });
    catchErrorWithStack(newError, true);
  }
  const profile = {
    uid: store.user.uid.trim(),
    uname: unameGlob.trim(),
    name: nameGlob && nameGlob.trim(),
    bdate: bdateGlob,
    aboutme: aboutmeGlob && aboutmeGlob.trim(),
    tiktok: tiktokGlob && tiktokGlob.trim(),
    reddit: redditGlob && redditGlob.trim(),
    discord: discordGlob && discordGlob.trim().toLowerCase(),
    ig: igGlob && igGlob.trim(),
    thumbnailUrl: downloadUrl || null,
  };
  let data = {
    ...profile,
    timestamp: Date.now(),
    metadata: {
      ...getEventParams(),
      language: navigator.language || navigator.userLanguage,
    },
  };
  data = Object.fromEntries(
    Object.entries(data).filter(([, v]) => v !== null && v !== undefined)
  );
  if (__DEV__) log.debug("Profile data", data);
  try {
    await withSpinner(
      cachedFirestore.setDocument({
        reference: `profile/${store.user.uid}`,
        data,
        merge: true,
      })
    );
  } catch (error) {
    throwErrorWithStack(error, true);
  }
  store.set("profile", { ...store.profile, ...profile });
  setProfileInfoToEls();
  menuProfilePhotoUnameEl.style.display = "block";
  profileEditorDialogEl.style.display = "none";
  updateProfile(profile);
  showInfoSnackbar("Profile saved");
};

addTouchEvent(profileEditorCancelEl, () => {
  profileEditorDialogEl.style.display = "none";
});
addTouchEvent(profileEditorOkEl, saveProfile);

export function setProfileInfoToEls() {
  menuProfilePhotoUnameEl.textContent = store.profile.uname;
  profileEditorPhotoEl.src =
    store.profile.thumbnailUrl || store.user.photoUrl || "";
  menuProfilePhotoImgEl.src =
    store.profile.thumbnailUrl || store.user.photoUrl || "";
  communityProfileFeedEl.src =
    store.profile.thumbnailUrl || store.user.photoUrl || "";
}

async function unameExists(uname) {
  try {
    const data = await cachedFirestore.getCollection({
      reference: "profile",
      compositeFilter: {
        type: "and",
        queryConstraints: [
          {
            type: "where",
            fieldPath: "uname",
            opStr: "==",
            value: uname,
          },
        ],
      },
    });
    if (!data.length) return false;
    if (data.length > 1) return true;
    if (data[0].id === store.user.uid) return false;
    return true;
  } catch (error) {
    catchErrorWithStack(error);
    return true;
  }
}
